import { useEffect, useId, useRef, useState, type KeyboardEvent } from "react";
import { ChevronRight } from "lucide-react";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import { useLanguage } from "../../i18n/language-provider";
import { useLocation } from "../../navigation/history";
import { routePath } from "../../navigation/routes";
import { calendarViewLabels } from "../drafts/calendarViewLabels";
import { AppLink } from "../navigation/AppLink";
import { RAIL_BESIDE_PAGE_QUERY } from "../RailFrame";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { cn } from "../ui/utils";
import { CalendarViewLinks } from "./CalendarViewLinks";
import { FLYOUT_ITEM_SELECTOR, focusAfter, moveFlyoutFocus } from "./flyoutKeys";
import { PRICES_PLACE_TABS, pricesTabRoute, type PricesFormProps } from "./prices-places";
import { offsetPastRail } from "./useRailFlyout";

const ROW =
    "flex h-6.5 w-full min-w-0 items-center rounded-md px-2 text-[0.8125rem] transition-colors duration-150 outline-none focus-visible:ring-[3px] focus-visible:ring-sidebar-ring/50";
const ROW_IDLE = "text-sidebar-foreground/80 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground";
const ROW_CURRENT = "bg-accent text-primary font-medium";
/** How far past the rail's border the views start. */
const VIEWS_RAIL_GAP_PX = 16;
const VIEWS_HEADING =
    "px-2 pt-1 pb-1.5 text-[0.6875rem] font-semibold uppercase tracking-[0.1em] text-muted-foreground";

/**
 * The open rail's branch under Prices: the page's three tabs on every page, and beside the calendar a button that
 * opens its views to the right of the rail on a click alone.
 */
export function PricesTree({
    currentTab,
    shownView,
    labels,
    connectionId,
    connectionVersion,
    readTracking,
    onOpenTab,
    onOpenView,
}: PricesFormProps) {
    const { t } = useLanguage();
    const viewNames = calendarViewLabels(t);
    const [viewsOpen, setViewsOpen] = useState(false);
    const [viewsOffset, setViewsOffset] = useState(VIEWS_RAIL_GAP_PX);
    const headingId = useId();
    const viewsButtonRef = useRef<HTMLButtonElement>(null);

    const { pathname, search } = useLocation();
    useEffect(() => {
        setViewsOpen(false);
    }, [pathname, search]);

    // The rail is hidden below md, not unmounted, and the portalled views would be left at the screen's corner.
    const besidePage = useMediaQuery(RAIL_BESIDE_PAGE_QUERY);
    useEffect(() => {
        if (!besidePage) setViewsOpen(false);
    }, [besidePage]);

    const onViewsKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        const unmodified = !event.altKey && !event.ctrlKey && !event.metaKey;
        if (event.key === "Tab" && unmodified && !event.shiftKey && viewsButtonRef.current) {
            // Radix hands no focus moved outside back to the button, so the next row keeps it.
            event.preventDefault();
            focusAfter(viewsButtonRef.current);
            setViewsOpen(false);
            return;
        }
        if ((event.key === "ArrowLeft" || (event.key === "Tab" && event.shiftKey)) && unmodified) {
            event.preventDefault();
            setViewsOpen(false);
            return;
        }
        moveFlyoutFocus(event, event.currentTarget);
    };

    return (
        <ul aria-label={labels.prices} className="ml-5 border-l border-sidebar-border pl-2">
            {PRICES_PLACE_TABS.map((tab) => {
                const current = currentTab === tab;
                const calendar = tab === "calendar";
                const trailing = calendar && current && shownView ? viewNames[shownView] : null;
                return (
                    <li key={tab} className="relative">
                        <AppLink
                            to={routePath(pricesTabRoute(tab))}
                            active={current}
                            onNavigate={() => onOpenTab(tab)}
                            className={cn(ROW, calendar && "pr-8", current ? ROW_CURRENT : ROW_IDLE)}
                        >
                            {/* The view's name gives way first. */}
                            <span className={cn("truncate", trailing && "shrink-0")}>{labels[tab]}</span>
                            {trailing && (
                                <span aria-hidden="true" className="ml-auto min-w-0 truncate pl-2 text-xs font-normal">
                                    {trailing}
                                </span>
                            )}
                        </AppLink>
                        {calendar && (
                            <Popover
                                open={viewsOpen}
                                onOpenChange={(open) => {
                                    if (open && viewsButtonRef.current) {
                                        setViewsOffset(offsetPastRail(viewsButtonRef.current, VIEWS_RAIL_GAP_PX));
                                    }
                                    setViewsOpen(open);
                                }}
                            >
                                {/* A list of links, not a dialog: Radix's `aria-haspopup="dialog"` is taken off. */}
                                <PopoverTrigger asChild aria-haspopup={undefined}>
                                    <button
                                        ref={viewsButtonRef}
                                        type="button"
                                        aria-label={labels.views}
                                        className={cn(
                                            "absolute right-0.5 top-1/2 -translate-y-1/2 inline-flex size-6 items-center justify-center rounded-sm outline-none focus-visible:ring-[3px] focus-visible:ring-sidebar-ring/50 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground aria-expanded:bg-sidebar-accent aria-expanded:text-sidebar-accent-foreground",
                                            // Rail foreground on the clay chip of the current row would not read.
                                            current ? "text-primary" : "text-sidebar-foreground/60",
                                        )}
                                    >
                                        <ChevronRight className="size-3.5" />
                                    </button>
                                </PopoverTrigger>
                                <PopoverContent
                                    side="right"
                                    align="start"
                                    sideOffset={viewsOffset}
                                    collisionPadding={8}
                                    role="group"
                                    aria-labelledby={headingId}
                                    className="w-56 max-h-(--radix-popover-content-available-height) overflow-y-auto rounded-lg p-1.5"
                                    onOpenAutoFocus={(event) => {
                                        event.preventDefault();
                                        const content = event.currentTarget as HTMLElement;
                                        const target =
                                            content.querySelector<HTMLElement>(`${FLYOUT_ITEM_SELECTOR}[aria-current="true"]`)
                                            ?? content.querySelector<HTMLElement>(FLYOUT_ITEM_SELECTOR);
                                        target?.focus();
                                    }}
                                    onKeyDown={onViewsKeyDown}
                                >
                                    <CalendarViewLinks
                                        layout="list"
                                        heading={{ id: headingId, text: labels.views }}
                                        shown={shownView}
                                        connectionId={connectionId}
                                        connectionVersion={connectionVersion}
                                        read={readTracking}
                                        fallback={
                                            <>
                                                {/* The popover is named by this heading, whatever stands in it. */}
                                                <p id={headingId} className={VIEWS_HEADING}>
                                                    {labels.views}
                                                </p>
                                                <p className="px-2 py-1.5 text-sm text-muted-foreground">
                                                    {labels.unavailableHint}
                                                </p>
                                            </>
                                        }
                                        onOpenView={(view) => {
                                            onOpenView(view);
                                            setViewsOpen(false);
                                        }}
                                    />
                                </PopoverContent>
                            </Popover>
                        )}
                    </li>
                );
            })}
        </ul>
    );
}
